import { omit, isEqual } from 'lodash'

const uiState_2026_02_05 = {
  currentView: 'timeline',
  currentTimeline: 1, // bookId or 'series'
  timelineIsExpanded: true,
  orientation: 'horizontal',
  darkMode: false, // <- Deprecated, but keep here for a while for backwards compatibility.
  characterSort: 'name~asc',
  characterFilter: {
    book: [],
    category: [],
    tag: [],
  },
  placeSort: 'name~asc',
  placeFilter: {
    book: [],
    tag: [],
  },
  noteSort: 'title~asc',
  noteFilter: {
    book: [],
    tag: [],
    character: [],
    place: [],
    category: [],
  },
  outlineFilter: null,
  timelineFilter: {
    tag: [],
    character: [],
    place: [],
    color: [],
  },
  timelineScrollPosition: {
    x: 0,
    y: 0,
  },
  timeline: {
    pinnedPlotlines: {},
    size: 'large',
    view: 'default',
    actTab: 0,
    focus: [],
    beatToDelete: null,
    contextMenuBeat: null,
    editingBeatId: null,
    beatHeadingTitleBeingEdited: null,
    plotlineTitleBeingEdited: null,
    smallTimelineCardColorSource: 'plotline',
  },
  searchTerms: {
    notes: null,
    characters: null,
    places: null,
    tags: null,
    outline: null,
    timeline: null,
    ideas: null,
    bookmarks: null,
  },
  attributeTabs: {
    characters: 'all',
  },
  characterTab: {
    selectedCharacter: null,
    focus: [],
    attributesDialogOpen: false,
    categoriesDialogOpen: false,
    editingSelected: false,
    showTemplatePicker: false,
    creating: false,
    templateData: null,
    filterVisible: false,
    sortVisible: false,
    detailsVisible: true,
    characterEditor: {
      deleting: false,
      removing: false,
      removeWhichTemplate: null,
      activeTab: 1,
      showTemplatePicker: false,
    },
    characterSelectedForChangingImage: null,
    relationshipEditorOpen: false,
    view: 'default',
  },
  customAttributeOrder: {
    characters: [],
  },
  cardDialog: {
    isOpen: false,
    cardId: null,
    lineId: null,
    beatId: null,
    deleting: false,
    showColorPicker: false,
    showTemplatePicker: false,
    removing: false,
    removeWhichTemplate: null,
  },
  collaborators: {
    collaborators: [],
    viewers: [],
  },
  bookDialog: {
    isOpen: false,
    bookId: null,
  },
  searchDialog: {
    isOpen: false,
    term: '',
    currentHitIndex: null,
    scanning: false,
    replacing: false,
    replacement: '',
    hitsToReplace: [],
    replaceWord: false,
    jumping: false,
    activeTab: 'search',
  },
  projectTab: {
    focus: [],
  },
  outlineTab: {
    focus: [],
    selectedCard: null,
    cardEditor: {
      editing: null,
    },
    layout: {
      view: 'plan',
      fulltext: {
        showCardTitle: true,
        showBeatTitle: true,
        showLayoutConfig: true,
        showBeatGaps: true,
        cardTitleAlignment: 'left',
        beatTitleAlignment: 'left',
        detailsPinned: false,
        zoom: 1,
        light: '2',
        hoverDetails: {
          lineTitle: null,
          beatTitle: null,
          cardTitle: null,
          cardId: null,
          visible: false,
        },
      },
      card: {
        colorSource: 'plotline',
      },
    },
  },
  noteTab: {
    focus: [],
    selectedNote: null,
    editingSelected: false,
    categoriesDialogOpen: false,
    attributesDialogOpen: false,
    filterVisible: false,
    sortVisible: false,
  },
  placeTab: {
    focus: [],
    selectedPlace: null,
    attributeDialogOpen: false,
    editingSelected: false,
    categoriesOpen: false,
    filterVisible: false,
    sortVisible: false,
  },
  tagTab: {
    focus: [],
    selectedTag: null,
    editingSelectedTab: false,
  },
  outlineScrollPosition: 0,
  templateModal: {
    expanded: false,
  },
  actConfigModal: {
    open: false,
  },
  resturctureTimelineModal: {
    open: false,
  },
  dashboardModal: {
    view: null,
  },
  conflictResolutionModal: {
    open: false,
  },
  importModal: {
    type: null,
    data: null,
    bookData: null,
    open: false,
    showProRecentFiles: false,
  },
  familyTree: {
    currentFamilyTree: 0,
    selectedRelationshipType: null,
    showColourPicker: false,
    stagePositions: {},
    idOfTreeWhoseNameIsBeingEdited: null,
  },
  documentation: {
    documentationType: 'advanced-export',
    open: false,
  },
  outlinePlanScrollPosition: 0,
  attributesDialogIsOpen: false,
  showPreviewPanels: true,
  previewPanels: {
    panels: [],
    showConfirmModal: false,
  },
  quickEditModal: {
    open: false,
    id: null,
    kind: null,
  },
  bookmarks: { items: [], collapsedSection: [] },
  ideaBoard: {
    open: false,
    ideas: [],
    modal: {
      open: false,
      id: null,
    },
    confirmModal: {
      open: false,
      id: null,
      isDeleting: false,
      section: null,
      data: null,
    },
    showCardDescription: true,
    shouldShowPanelsWithIdeaBoard: false,
  },
  theme: null,
}

/**
 * @param {Array<any>} characters
 * @return {Array<any>}
 */
const addAttributesCollectionIfMissing = (characters) => {
  const isMissingSomewhere = characters.some((character) => {
    return !Array.isArray(character.attributes)
  })
  if (isMissingSomewhere) {
    return characters.map((character) => {
      if (Array.isArray(character.attributes)) {
        return character
      } else {
        return {
          ...character,
          attributes: [],
        }
      }
    })
  } else {
    return characters
  }
}

/**
 * Add all missing state as of 2026/02/05 to a Plottr file.
 *
 * This special case fixer is intended to remedy problems introduced
 * by not adding migrators when new state was added to the file and
 * instead relying too heavily on selectors to default missing values.
 *
 * As a rule, we should only default ephemeral values.  i.e. those
 * that come from the ApplicationState.
 *
 * @param {any} file
 * @return {any}
 */
function migrate(file) {
  /**
   * @param {any} referenceNode
   * @param {any} inputNode
   * @return {any}
   */
  function iterFillInMissingState(referenceNode, inputNode) {
    return Object.keys(referenceNode).reduce((acc, nextKey) => {
      if (typeof inputNode[nextKey] === 'undefined') {
        return {
          ...acc,
          [nextKey]: referenceNode[nextKey],
        }
      } else if (
        referenceNode[nextKey] &&
        typeof referenceNode[nextKey] === 'object' &&
        inputNode[nextKey]
      ) {
        return {
          ...acc,
          [nextKey]: iterFillInMissingState(referenceNode[nextKey], inputNode[nextKey]),
        }
      } else {
        return {
          ...acc,
          [nextKey]: inputNode[nextKey],
        }
      }
    }, inputNode)
  }

  /**
   * @param {any} uiState
   * @return {any}
   */
  function processClientUiState(uiState) {
    const defaultedUiState = iterFillInMissingState(uiState_2026_02_05, uiState)
    const shouldUpdate = !isEqual(
      omit(uiState, 'collaborators'),
      omit(defaultedUiState, 'collaborators')
    )

    return [shouldUpdate, defaultedUiState]
  }

  const [shouldUpdateRootUiState, defaultedUiState] = processClientUiState(file.ui)

  const [shouldUpdateCollaborators, updatedCollaborators] = !file.ui.collaborators?.collaborators
    ? [true, []]
    : file.ui.collaborators.collaborators.reduce(
        (acc, next) => {
          const [shouldUpdate, collaborators] = acc
          const [shouldUpdateCollaborator, defaultedCollaboratorUiState] =
            processClientUiState(next)
          return [
            shouldUpdate || shouldUpdateCollaborator,
            [
              ...collaborators,
              shouldUpdateCollaborator ? omit(defaultedCollaboratorUiState, 'collaborators') : next,
            ],
          ]
        },
        [false, []]
      )

  const [shouldUpdateViewers, updatedViewers] = !file.ui.collaborators?.viewers
    ? [true, []]
    : file.ui.collaborators.viewers.reduce(
        (acc, next) => {
          const [shouldUpdate, viewers] = acc
          const [shouldUpdateViewer, defaultedViewerUiState] = processClientUiState(next)
          return [
            shouldUpdate || shouldUpdateViewer,
            [...viewers, shouldUpdateViewer ? omit(defaultedViewerUiState, 'collaborators') : next],
          ]
        },
        [false, []]
      )

  /**
   * @typedef MightHaveIncorrectAudioClipAttribute
   * @property {number | Null} [audioClip]
   * @property {number | Null} [audioClipId]
   */

  /**
   * @param {MightHaveIncorrectAudioClipAttribute} entity
   * @return {MightHaveIncorrectAudioClipAttribute}
   */
  const fixAudioClipAttributeNameIfIncorrect = (entity) => {
    if ('audioClip' in entity) {
      if ('audioClipId' in entity) {
        return omit(entity, 'audioClip')
      } else if (entity.audioClip === null || typeof entity.audioClip === 'number') {
        return {
          ...omit(entity, 'audioClip'),
          audioClipId: entity.audioClip,
        }
      } else if (!('audioClipId' in entity)) {
        return {
          ...omit(entity, 'audioClip'),
          audioClipId: null,
        }
      } else {
        return entity
      }
    } else if (
      !('audioClipId' in entity) ||
      (entity.audioClipId !== null && typeof entity.audioClipId !== 'number')
    ) {
      return {
        ...entity,
        audioClipId: null,
      }
    } else {
      return entity
    }
  }

  /**
   * NOTE(ed): if we're in a template, there may not be characters,
   * notes, places and cards, so check if they're there before fixing
   * them.
   */
  const defaultedAndFixedCharacters = addAttributesCollectionIfMissing(file.characters ?? []).map(
    fixAudioClipAttributeNameIfIncorrect
  )
  const shouldUpdateCharacters =
    'characters' in file && !isEqual(file.characters, defaultedAndFixedCharacters)

  const fixedNotes = (file.notes ?? []).map(fixAudioClipAttributeNameIfIncorrect)
  const shouldUpdateNotes = 'notes' in file && !isEqual(file.notes, fixedNotes)

  const fixedPlaces = (file.places ?? []).map(fixAudioClipAttributeNameIfIncorrect)
  const shouldUpdatePlaces = 'places' in file && !isEqual(file.places, fixedPlaces)

  const fixedCards = (file.cards ?? []).map(fixAudioClipAttributeNameIfIncorrect)
  const shouldUpdateCards = !isEqual(file.cards, fixedCards)

  const fixMissingColorAndCategoryId = (tag) => {
    return {
      ...tag,
      color: 'color' in tag ? tag.color : null,
      categoryId: 'categoryId' in tag ? tag.categoryId : null,
    }
  }

  const fixedTags = (file.tags ?? []).map(fixMissingColorAndCategoryId)
  const shouldUpdateTags = !isEqual(file.tags, fixedTags)

  if (
    shouldUpdateRootUiState ||
    shouldUpdateCollaborators ||
    shouldUpdateViewers ||
    shouldUpdateCharacters ||
    shouldUpdateNotes ||
    shouldUpdatePlaces ||
    shouldUpdateCards ||
    shouldUpdateTags
  ) {
    return {
      ...file,
      ui: {
        ...(shouldUpdateRootUiState ? defaultedUiState : file.ui),
        collaborators: {
          collaborators: shouldUpdateCollaborators
            ? updatedCollaborators
            : (file.collaborators?.collaborators ?? []),
          viewers: shouldUpdateViewers ? updatedViewers : (file.collaborators?.viewers ?? []),
        },
      },
      ...(file.characters
        ? { characters: shouldUpdateCharacters ? defaultedAndFixedCharacters : file.characters }
        : {}),
      ...(file.notes ? { notes: shouldUpdateNotes ? fixedNotes : file.notes } : {}),
      ...(file.places ? { places: shouldUpdatePlaces ? fixedPlaces : file.places } : {}),
      ...(file.cards ? { cards: shouldUpdateCards ? fixedCards : file.cards } : {}),
      ...(file.tags ? { tags: shouldUpdateTags ? fixedTags : file.tags } : {}),
    }
  } else {
    return file
  }
}

export default migrate
